/**
 * sl-9qm.4.2: CardGrid Component
 *
 * Cards in responsive CSS grid. Each card shows cover image,
 * title, excerpt, tags, and date.
 */
import type { CustomAppConfig, PageDefinition } from '@streamlain/view-config';
import type { MouseEvent } from 'react';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  BoundDataRow,
  ViewComponent,
  ViewComponentRenderProps,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { ResponsiveImage } from '../../responsive-image/responsive-image';
import * as styles from '../styles.css';
import { getContentCopy } from './content-copy';

const configSchema = z.object({
  columns: z
    .union([z.literal('auto'), z.literal(2), z.literal(3), z.literal(4)])
    .default('auto'),
  featuredFirst: z.boolean().default(false),
  featuredLabel: z.string().default('Featured article'),
  gridLabel: z.string().default('Latest articles'),
  showImage: z.boolean().default(true),
  showExcerpt: z.boolean().default(true),
  showTags: z.boolean().default(true),
  teaserSource: z.enum(['data', 'child-routes']).default('data'),
});

function getGridColumns(columns: 'auto' | 2 | 3 | 4): string {
  if (columns === 'auto') return 'repeat(auto-fill, minmax(280px, 1fr))';
  return `repeat(${columns}, 1fr)`;
}

function readCell(row: BoundDataRow, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined) return value;
  }
  return undefined;
}

function readString(
  row: BoundDataRow,
  keys: readonly string[]
): string | undefined {
  const value = readCell(row, keys);
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

function readTags(row: BoundDataRow): string[] {
  const value = readCell(row, [
    'tags',
    'tag',
    'categories',
    'category',
    'topicId',
    'clusterId',
  ]);
  const rawValues = Array.isArray(value) ? value : [value];
  return rawValues
    .flatMap(item => {
      if (typeof item === 'string') return item.split(',');
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const label =
          record['name'] ?? record['title'] ?? record['label'] ?? record['id'];
        return typeof label === 'string' ? [label] : [];
      }
      return [];
    })
    .map(formatTagLabel)
    .filter((tag, index, all) => tag && all.indexOf(tag) === index);
}

function formatTagLabel(value: string): string {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, match => match.toUpperCase());
}

function renderTags(tags: readonly string[]) {
  if (tags.length === 0) return null;
  return tags.map(tag =>
    createElement('span', { key: tag, 'data-sl-field-tag': tag }, tag)
  );
}

function renderCard(
  row: BoundDataRow,
  config: z.infer<typeof configSchema>,
  index: number,
  variant: 'card' | 'featured' = 'card',
  workspaceId?: string
) {
  const title = readString(row, ['title', 'name']) ?? 'Untitled';
  const cover = readCell(row, ['cover', 'image', 'thumbnail']);
  const alt = readString(row, ['alt', 'imageAlt', 'coverAlt']) ?? '';
  const excerpt = readString(row, ['excerpt', 'summary', 'description']);
  const tags = readTags(row);
  const date = readString(row, ['date', 'publishedAt', 'scheduledAt']);
  const hasCover = cover != null && cover !== '';
  const isFeatured = variant === 'featured';

  return createElement(
    'div',
    {
      key: index,
      className: isFeatured ? styles.featuredCard : styles.card,
      'data-sl-role': isFeatured ? 'featured-article' : 'card',
      ...(isFeatured && !hasCover
        ? { style: { gridTemplateColumns: '1fr' } }
        : {}),
    },
    config.showImage &&
      hasCover &&
      createElement(
        'div',
        {
          className: isFeatured ? styles.featuredCardMedia : styles.cardImage,
          'data-sl-field': 'image',
        },
        createElement(ResponsiveImage, {
          image: cover,
          role: 'card',
          alt,
          workspaceId,
          allowWorkspaceBlobFallback: Boolean(workspaceId),
          className: styles.cardImageFill,
        })
      ),
    createElement(
      'div',
      { className: isFeatured ? styles.featuredCardBody : styles.cardBody },
      createElement(
        'div',
        { className: styles.cardTitle, 'data-sl-field': 'title' },
        title
      ),
      config.showExcerpt &&
        excerpt &&
        createElement(
          'div',
          { className: styles.cardExcerpt, 'data-sl-field': 'excerpt' },
          excerpt
        ),
      createElement(
        'div',
        { className: styles.cardMeta },
        config.showTags && renderTags(tags),
        date && createElement('span', { 'data-sl-field': 'date' }, date)
      )
    )
  );
}

function renderFeaturedGrid(
  data: BoundDataRow[],
  config: z.infer<typeof configSchema>,
  workspaceId?: string
) {
  const [featured, ...cards] = data;
  if (!featured) return null;
  return createElement(
    'div',
    {
      className: styles.cardGridWithFeature,
      'data-sl-role': 'kb-gallery',
    },
    config.featuredLabel
      ? createElement(
          'div',
          { className: styles.cardSectionLabel },
          config.featuredLabel
        )
      : null,
    renderCard(featured, config, 0, 'featured', workspaceId),
    cards.length > 0 && config.gridLabel
      ? createElement(
          'div',
          { className: styles.cardSectionLabel },
          config.gridLabel
        )
      : null,
    cards.length > 0
      ? createElement(
          'div',
          {
            className: styles.cardGrid,
            style: { gridTemplateColumns: getGridColumns(config.columns) },
          },
          ...cards.map((row, i) =>
            renderCard(row, config, i + 1, 'card', workspaceId)
          )
        )
      : null
  );
}

function readCustomAppConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): CustomAppConfig | null {
  const raw = runtimeContext?.customApp;
  const config =
    raw && typeof raw === 'object' && 'config' in raw
      ? (raw as { config?: unknown }).config
      : null;
  return config &&
    typeof config === 'object' &&
    Array.isArray((config as CustomAppConfig).pages)
    ? (config as CustomAppConfig)
    : null;
}

function readCurrentPage(
  runtimeContext: ViewComponentRenderProps['runtimeContext'],
  customAppConfig: CustomAppConfig | null
): PageDefinition | null {
  const raw = runtimeContext?.page;
  if (!raw || typeof raw !== 'object') return null;
  const page = raw as Partial<PageDefinition> & { pageId?: unknown };
  if (typeof page.id === 'string') return page as PageDefinition;
  if (typeof page.pageId !== 'string' || !customAppConfig) return null;
  return (
    customAppConfig.pages.find(candidate => candidate.id === page.pageId) ??
    null
  );
}

function resolvePageLabel(page: PageDefinition): string {
  const formula = page.titleFormula;
  if (formula?.type === 'value' && typeof formula.value === 'string') {
    const label = formula.value.trim();
    if (label) return label;
  }
  if (page.path === '/') return 'Start';
  const segment = page.path.split('/').filter(Boolean).at(-1);
  return segment ? formatTagLabel(segment) : page.id;
}

function resolveRouteTeaserDescription(page: PageDefinition): string {
  switch (page.templateKey) {
    case 'knowledgebase-index':
      return 'Guides, Kategorien und Artikel fuer strukturiertes Wissen.';
    case 'blog-index':
      return 'Aktuelle Updates, Artikel und Ankuendigungen.';
    case 'intranet-dashboard':
      return 'KPIs, Status, Owner und operative Tabellen fuer interne Arbeit.';
    case 'workflow-form':
      return 'Strukturierte Eingaben und Requests direkt im App-Kontext.';
    case 'landing':
      return 'Uebersicht mit Einstieg in weitere Bereiche dieser App.';
    default:
      return page.audience === 'internal'
        ? 'Interner Bereich mit eigenen Inhalten und Daten.'
        : 'Eigener Bereich mit passenden Inhalten und Navigation.';
  }
}

function resolveRouteTeaserEyebrow(page: PageDefinition): string {
  switch (page.templateKey) {
    case 'knowledgebase-index':
      return 'Knowledge Base';
    case 'blog-index':
      return 'Aktuelles';
    case 'intranet-dashboard':
      return 'Dashboard';
    case 'workflow-form':
      return 'Workflow';
    case 'landing':
      return 'Landing Page';
    default:
      return page.audience === 'internal' ? 'Interner Bereich' : 'Bereich';
  }
}

function resolveRouteTeaserPreviewRows(
  page: PageDefinition
): Array<{ label: string; value: string }> {
  switch (page.templateKey) {
    case 'knowledgebase-index':
      return [
        { label: 'Erste Schritte', value: 'Guide' },
        { label: 'Automatisierungen', value: 'Artikel' },
        { label: 'Themen', value: 'Suche' },
      ];
    case 'blog-index':
      return [
        { label: 'Produkt-Update', value: 'News' },
        { label: 'Roadmap', value: 'Entwurf' },
        { label: 'Archiv', value: 'Artikel' },
      ];
    case 'intranet-dashboard':
      return [
        { label: 'Aktive Arbeit', value: '42' },
        { label: 'Blocker', value: '2' },
        { label: 'Owner', value: 'Teams' },
      ];
    case 'workflow-form':
      return [
        { label: 'Eingabe', value: 'Form' },
        { label: 'Status', value: 'Neu' },
        { label: 'Automation', value: 'Trigger' },
      ];
    default:
      return [
        { label: 'Inhalte', value: 'Bereit' },
        { label: 'Route', value: page.path },
        { label: 'SEO', value: 'Auto' },
      ];
  }
}

function listChildRouteTeasers(
  customAppConfig: CustomAppConfig,
  currentPage: PageDefinition
): PageDefinition[] {
  return customAppConfig.pages
    .filter(page => (page.parentPageId ?? null) === currentPage.id)
    .filter(page => page.routeKind !== 'detail')
    .filter(page => !page.path.includes(':'))
    .sort((a, b) => {
      const orderDelta =
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER);
      return orderDelta || a.path.localeCompare(b.path);
    });
}

function renderChildRouteTeaserGrid(
  pages: readonly PageDefinition[],
  config: z.infer<typeof configSchema>,
  mode: ViewComponentRenderProps['mode']
) {
  return createElement(
    'div',
    {
      id: 'bereiche',
      className: styles.routeTeaserSections,
      'data-sl-role': 'route-teaser-sections',
    },
    ...pages.map(page => {
      const title = resolvePageLabel(page);
      const tags = [
        formatTagLabel(page.templateKey),
        page.audience === 'internal' ? 'Intern' : 'Public',
      ];
      return createElement(
        'a',
        {
          key: page.id,
          className: styles.routeTeaserSection,
          href: page.path,
          'data-sl-role': 'route-teaser',
          'data-sl-route-id': page.id,
          onClick: (event: MouseEvent<HTMLAnchorElement>) => {
            if (mode === 'builder' || mode === 'preview') {
              event.preventDefault();
            }
          },
        },
        createElement(
          'div',
          { className: styles.routeTeaserCopy },
          createElement(
            'div',
            { className: styles.routeTeaserEyebrow },
            resolveRouteTeaserEyebrow(page)
          ),
          createElement(
            'h2',
            { className: styles.routeTeaserTitle, 'data-sl-field': 'title' },
            title
          ),
          config.showExcerpt
            ? createElement(
                'p',
                {
                  className: styles.routeTeaserDescription,
                  'data-sl-field': 'excerpt',
                },
                resolveRouteTeaserDescription(page)
              )
            : null,
          createElement(
            'div',
            { className: styles.routeTeaserMeta },
            ...(config.showTags
              ? tags.map(tag =>
                  createElement(
                    'span',
                    { key: tag, className: styles.routeTeaserMetaItem },
                    tag
                  )
                )
              : []),
            createElement(
              'span',
              {
                className: styles.routeTeaserMetaItem,
                'data-sl-field': 'path',
              },
              page.path
            )
          )
        ),
        createElement(
          'div',
          {
            className: styles.routeTeaserPreview,
            'aria-hidden': 'true',
          },
          ...resolveRouteTeaserPreviewRows(page).map(row =>
            createElement(
              'div',
              {
                key: `${page.id}:${row.label}`,
                className: styles.routeTeaserPreviewRow,
              },
              createElement('span', {
                className: styles.routeTeaserPreviewIcon,
              }),
              createElement('span', null, row.label),
              createElement(
                'span',
                { className: styles.routeTeaserPreviewValue },
                row.value
              )
            )
          )
        )
      );
    })
  );
}

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.customApp?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}

export const CardGridComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.CARD_GRID,
  type: 'content',
  name: 'Card Grid',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '▦'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Card data rows',
      required: true,
      requirements: [
        { field: 'title', acceptedTypes: ['text'], required: true },
        {
          field: 'cover',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        { field: 'alt', acceptedTypes: ['text'], required: false },
        { field: 'excerpt', acceptedTypes: ['text'], required: false },
        {
          field: 'tags',
          acceptedTypes: ['multi-select', 'select'],
          required: false,
        },
        { field: 'date', acceptedTypes: ['date'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    columns: 'auto',
    featuredFirst: false,
    featuredLabel: 'Featured article',
    gridLabel: 'Latest articles',
    showImage: true,
    showExcerpt: true,
    showTags: true,
    teaserSource: 'data',
  },
  render({ config, data, dataBindingConfig, mode, runtimeContext }) {
    const typedConfig = config as z.infer<typeof configSchema>;
    const workspaceId = readWorkspaceId(runtimeContext);
    if (typedConfig.teaserSource === 'child-routes') {
      const customAppConfig = readCustomAppConfig(runtimeContext);
      const currentPage = readCurrentPage(runtimeContext, customAppConfig);
      const childRoutes =
        customAppConfig && currentPage
          ? listChildRouteTeasers(customAppConfig, currentPage)
          : [];
      if (childRoutes.length > 0) {
        return renderChildRouteTeaserGrid(childRoutes, typedConfig, mode);
      }
    }
    const emptyMessage = dataBindingConfig?.databaseDocId
      ? getContentCopy(runtimeContext?.locale, 'emptyRows')
      : mode === 'builder'
        ? getContentCopy(runtimeContext?.locale, 'notBound')
        : getContentCopy(runtimeContext?.locale, 'unavailable');
    if (data.length > 0 && typedConfig.featuredFirst) {
      return renderFeaturedGrid(data, typedConfig, workspaceId);
    }
    return createElement(
      'div',
      {
        className: styles.cardGrid,
        style: { gridTemplateColumns: getGridColumns(typedConfig.columns) },
      },
      data.length > 0
        ? data.map((row, i) =>
            renderCard(row, typedConfig, i, 'card', workspaceId)
          )
        : createElement(
            'div',
            {
              style: {
                padding: 'var(--sl-custom-app-padding-lg)',
                color: 'var(--streamlain-text-secondary-color)',
                textAlign: 'center' as const,
              },
            },
            emptyMessage
          )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '▦ Card Grid'
    );
  },
};
